<script setup>
/* eslint-disable camelcase */
import CustomConfirmationDialog from '@/components/common/CustomConfirmationDialog.vue'
import CustomDatatable from '@/components/common/CustomDatatable.vue'
import CustomForm from '@/components/common/CustomForm.vue'
import axios from '@/utils/axios'
import { toast } from 'vue3-toastify'

const branchCodes = ref({ data: [], total: 0 })
const route = useRoute()
const companies = ref([])
const repCodes = ref([])
const filters = ref({ page: 1, perPage: 10 })
const formRef = ref()
const formDialogVisible = ref(false)
const viewDialogVisible = ref(false)
const deleteDialogVisible = ref(false)
const saving = ref(false)
const selectedBranchCode = ref(null)
const formData = ref(emptyForm())

const userData = JSON.parse(localStorage.getItem('userData') || '{}')
const permissions = Object.values(userData.permissions_by_roles || {}).flat()
const canCreate = permissions.includes('Create Branch Code')
const canEdit = permissions.includes('Edit Branch Code')
const canDelete = permissions.includes('Delete Branch Code')

const headers = [
  { title: 'Branch Code', key: 'branch_code', sortable: true },
  { title: 'Investment Company', key: 'investment_company', sortable: true },
  { title: 'Associated Rep Codes', key: 'rep_codes_count', sortable: true },
  { title: 'Description / Notes', key: 'description', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false },
]

function emptyForm() {
  return {
    id: null,
    branch_code: '',
    investment_company_id: null,
    description: '',
    rep_code_ids: [],
  }
}

const companyOptions = computed(() => companies.value.map(company => ({
  title: company.title,
  value: company.id,
})))

const repCodeOptions = computed(() => repCodes.value.map(repCode => ({
  title: repCode.rep_code,
  value: repCode.id,
})))

const branchCodeRules = [
  value => !!value || 'Branch code is required',
  value => /^[A-Za-z0-9]+$/.test(value || '') || 'Use letters and numbers only',
  value => String(value || '').length <= 12 || 'Maximum 12 characters',
]

async function fetchReferenceData() {
  const companyResponse = await axios.get('/investment_companies?all=1')

  if (companyResponse.status === 'success')
    companies.value = companyResponse.data.investment_companies || []

}

async function fetchRepCodes(companyId) {
  repCodes.value = []
  if (!companyId)
    return

  const response = await axios.get('/rep_codes', {
    params: { investment_company_id: companyId, per_page: 250 },
  })

  if (response.status === 'success')
    repCodes.value = response.data.rep_codes?.data || []
}

function companyChanged(companyId) {
  formData.value.rep_code_ids = []
  fetchRepCodes(companyId)
}

async function fetchBranchCodes(payload = filters.value) {
  filters.value = { ...filters.value, ...payload }

  const params = {
    page: filters.value.page || 1,
    per_page: filters.value.perPage || 10,
    search_query: filters.value.searchQuery || undefined,
    sort_by: filters.value.sortBy || undefined,
    order_by: filters.value.orderBy || undefined,
    investment_company_id: filters.value.investmentCompanyId || undefined,
  }

  const response = await axios.get('/branch_codes', { params })
  if (response.status === 'success')
    branchCodes.value = response.data.branch_codes
  else
    toast.error(response.message)
}

function tableChanged(payload) {
  fetchBranchCodes(payload)
}

function searchValue(searchQuery) {
  fetchBranchCodes({ ...filters.value, page: 1, searchQuery })
}

async function addItem() {
  formData.value = emptyForm()
  formData.value.investment_company_id = route.query.investment_company_id
    ? Number(route.query.investment_company_id)
    : null
  await fetchRepCodes(formData.value.investment_company_id)
  formDialogVisible.value = true
}

async function editItem(item) {
  formData.value = {
    id: item.id,
    branch_code: item.branch_code,
    investment_company_id: item.investment_company_id,
    description: item.description || '',
    rep_code_ids: [...(item.rep_code_ids || [])],
  }
  await fetchRepCodes(item.investment_company_id)
  formDialogVisible.value = true
}

function viewItem(item) {
  selectedBranchCode.value = item
  viewDialogVisible.value = true
}

function deleteItem(item) {
  selectedBranchCode.value = item
  deleteDialogVisible.value = true
}

async function submitForm() {
  const validation = await formRef.value?.validate()
  if (!validation?.valid)
    return

  saving.value = true
  try {
    const payload = {
      ...formData.value,
      branch_code: formData.value.branch_code.trim().toUpperCase(),
    }

    const response = payload.id
      ? await axios.put(`/branch_codes/${payload.id}`, payload)
      : await axios.post('/branch_codes', payload)

    if (response.status === 'success') {
      toast.success(response.message)
      formDialogVisible.value = false
      await fetchBranchCodes()
    } else {
      toast.error(response.message)
    }
  } finally {
    saving.value = false
  }
}

async function confirmDelete(confirmed) {
  if (!confirmed) {
    deleteDialogVisible.value = false

    return
  }

  const response = await axios.delete(`/branch_codes/${selectedBranchCode.value.id}`)
  if (response.status === 'success') {
    toast.success(response.message)
    deleteDialogVisible.value = false
    await fetchBranchCodes()
  } else {
    toast.error(response.message)
  }
}

onMounted(async () => {
  if (route.query.investment_company_id)
    filters.value.investmentCompanyId = Number(route.query.investment_company_id)

  await Promise.all([fetchReferenceData(), fetchBranchCodes()])
})

definePage({ meta: { requiresAuth: true } })
</script>

<template>
  <div>
    <div class="mb-4">
      <h3 class="text-h3 text-high-emphasis mb-1">
        Branch Code Management
      </h3>
      <div>Manage Branch Code Groups and associate existing Rep Codes.</div>
    </div>

    <VCard>
      <VCardText class="pb-0">
        <AppSelect
          v-model="filters.investmentCompanyId"
          :items="companyOptions"
          label="Investment Company"
          placeholder="All investment companies"
          clearable
          style="max-inline-size: 360px"
          @update:model-value="fetchBranchCodes({ ...filters, page: 1, investmentCompanyId: $event })"
        />
      </VCardText>
      <VCardText class="pa-0">
        <CustomDatatable
          :headers="headers"
          :table-data="branchCodes"
          @table-changed="tableChanged"
          @search-value="searchValue"
        >
          <template #add_button>
            <VBtn
              v-if="canCreate"
              prepend-icon="tabler-plus"
              @click="addItem"
            >
              Add Branch Code Group
            </VBtn>
          </template>

          <template #item.branch_code="{ item }">
            <span class="font-weight-medium text-primary">{{ item.branch_code }}</span>
          </template>

          <template #item.investment_company="{ item }">
            {{ item.investment_company?.title || '—' }}
          </template>

          <template #item.rep_codes_count="{ item }">
            <VChip
              v-for="repCode in item.rep_codes"
              :key="repCode.id"
              size="small"
              color="primary"
              variant="tonal"
              class="me-1 mb-1"
            >
              {{ repCode.rep_code }}
            </VChip>
            <span
              v-if="!item.rep_codes?.length"
              class="text-medium-emphasis"
            >
              —
            </span>
          </template>

          <template #item.description="{ item }">
            <span class="text-wrap">{{ item.description || '—' }}</span>
          </template>

          <template #table_actions="{ item }">
            <div class="d-flex gap-1">
              <VBtn
                size="x-small"
                color="primary"
                @click="viewItem(item)"
              >
                <VIcon icon="tabler-eye" />
              </VBtn>
              <VBtn
                v-if="canEdit"
                size="x-small"
                color="primary"
                @click="editItem(item)"
              >
                <VIcon icon="tabler-pencil" />
              </VBtn>
              <VBtn
                v-if="canDelete"
                size="x-small"
                color="error"
                @click="deleteItem(item)"
              >
                <VIcon icon="tabler-trash" />
              </VBtn>
            </div>
          </template>
        </CustomDatatable>
      </VCardText>
    </VCard>

    <CustomForm
      v-model:is-dialog-visible="formDialogVisible"
      :is-dialog-visible="formDialogVisible"
      :title="formData.id ? 'Edit Branch Code Group' : 'Add Branch Code Group'"
      description="Set the investment company and associate one or more existing Rep Codes."
      :width="700"
    >
      <template #form>
        <VForm
          ref="formRef"
          class="mt-6"
          @submit.prevent="submitForm"
        >
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <AppTextField
                v-model="formData.branch_code"
                label="Branch Code *"
                placeholder="Up to 12 letters or numbers"
                maxlength="12"
                :counter="12"
                :rules="branchCodeRules"
                @update:model-value="formData.branch_code = String($event || '').toUpperCase()"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <AppSelect
                v-model="formData.investment_company_id"
                :items="companyOptions"
                label="Investment Company *"
                placeholder="Select a company"
                :rules="[value => !!value || 'Investment company is required']"
                @update:model-value="companyChanged"
              />
            </VCol>
            <VCol cols="12">
              <AppCombobox
                v-model="formData.rep_code_ids"
                :items="repCodeOptions"
                label="Associated Rep Codes"
                placeholder="Search and select Rep Codes"
                multiple
                chips
                closable-chips
                :return-object="false"
                :disabled="!formData.investment_company_id"
              />
            </VCol>
            <VCol cols="12">
              <AppTextarea
                v-model="formData.description"
                label="Description / Notes"
                placeholder="Optional notes"
                rows="4"
              />
            </VCol>
            <VCol
              cols="12"
              class="d-flex justify-end gap-3"
            >
              <VBtn
                color="secondary"
                variant="tonal"
                @click="formDialogVisible = false"
              >
                Cancel
              </VBtn>
              <VBtn
                type="submit"
                :loading="saving"
              >
                {{ formData.id ? 'Update' : 'Create' }}
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </template>
    </CustomForm>

    <VDialog
      v-model="viewDialogVisible"
      max-width="650"
    >
      <VCard title="Branch Code Group Details">
        <VCardText v-if="selectedBranchCode">
          <VList>
            <VListItem
              title="Branch Code"
              :subtitle="selectedBranchCode.branch_code"
            />
            <VListItem
              title="Investment Company"
              :subtitle="selectedBranchCode.investment_company?.title || '—'"
            />
            <VListItem
              title="Description / Notes"
              :subtitle="selectedBranchCode.description || '—'"
            />
          </VList>
          <div class="text-subtitle-1 font-weight-medium mt-3 mb-2">
            Associated Rep Codes
          </div>
          <VChip
            v-for="repCode in selectedBranchCode.rep_codes"
            :key="repCode.id"
            class="me-2 mb-2"
            color="primary"
            variant="tonal"
          >
            {{ repCode.rep_code }}
          </VChip>
          <div
            v-if="!selectedBranchCode.rep_codes?.length"
            class="text-medium-emphasis"
          >
            No Rep Codes associated.
          </div>
        </VCardText>
        <VCardActions class="justify-end">
          <VBtn
            variant="tonal"
            @click="viewDialogVisible = false"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <CustomConfirmationDialog
      :is-dialog-visible="deleteDialogVisible"
      title="Delete this Branch Code Group?"
      description="Associated Rep Codes will be safely unlinked before the group is deleted."
      icon="tabler-exclamation-circle"
      icon-color="error"
      confirm-button-text="Delete"
      confirm-button-color="error"
      cancel-button-text="Cancel"
      @confirmation="confirmDelete"
    />
  </div>
</template>
